import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@anyseal/api-client";
import {
  Badge,
  Button,
  Field,
  Input,
  MetricCard,
  PageHeader,
  PageSection,
  RequestId,
  Row,
  StatePanel,
  formatDate,
  formatNumber,
} from "@anyseal/ui";
import { CalendarCheck, Copy } from "lucide-react";
import { portalClient } from "../api";
import { useI18n } from "../i18n";
import { projectPath, useWorkspace } from "../workspace";
import { WorkspaceGuard } from "../WorkspaceGuard";

interface ReferralProfile {
  referral_code: string;
  successful_referrals: number;
  earned_microunits: number;
  created_at: string;
}
interface Checkin {
  id: string;
  date: string;
  awarded_microunits: number;
  available_microunits?: number;
  created_at: string;
}
interface CheckinStatus {
  enabled: boolean;
  checked_in_today: boolean;
  month_count: number;
  month_award_microunits: number;
  recent: Checkin[];
}
interface RedeemResult {
  voucher_id: string;
  credit_microunits: number;
  available_microunits: number;
}

export function GrowthPageInner() {
  const { locale } = useI18n();
  const { organization, project } = useWorkspace();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [notice, setNotice] = useState("");

  const base = organization && project ? projectPath(organization.id, project.id) : "";
  const currency = organization?.default_currency ?? "CNY";

  const referral = useQuery({
    queryKey: ["portal", "growth", "referral", base],
    queryFn: () => portalClient.get<ReferralProfile>(`${base}/growth/referral`),
    enabled: Boolean(base),
    retry: false,
  });

  const checkins = useQuery({
    queryKey: ["portal", "growth", "checkins", base],
    queryFn: () => portalClient.get<CheckinStatus>(`${base}/growth/checkins`),
    enabled: Boolean(base),
  });

  const checkin = useMutation({
    mutationFn: () => portalClient.post<Checkin>(`${base}/growth/checkins`),
    onSuccess: (result) => {
      setNotice(
        `${locale === "zh-CN" ? "本次签到获得" : "Check-in awarded"} ${formatNumber(result.awarded_microunits / 1_000_000, locale)} ${locale === "zh-CN" ? "额度" : "credit"}`,
      );
      void queryClient.invalidateQueries({ queryKey: ["portal", "growth", "checkins", base] });
      void queryClient.invalidateQueries({ queryKey: ["portal", "billing", base] });
    },
  });

  const redeem = useMutation({
    mutationFn: () => portalClient.post<RedeemResult>(`${base}/vouchers/redeem`, { code: code.trim() }),
    onSuccess: (result) => {
      setNotice(
        `${locale === "zh-CN" ? "兑换成功" : "Voucher redeemed"}: ${formatNumber(result.credit_microunits / 1_000_000, locale)}`,
      );
      setCode("");
      void queryClient.invalidateQueries({ queryKey: ["portal", "billing", base] });
    },
  });

  const failure =
    checkins.error ??
    (referral.error instanceof ApiError && referral.error.code !== "growth_program_disabled"
      ? referral.error
      : null);

  const actionError = checkin.error ?? redeem.error;

  if (failure) return (
    <StatePanel
      kind="error"
      title={locale === "zh-CN" ? "奖励信息暂不可用" : "Rewards are unavailable"}
      description={failure.message}
      requestId={failure instanceof ApiError ? failure.requestId : undefined}
      action={
        <Button
          variant="secondary"
          onClick={() => { void referral.refetch(); void checkins.refetch(); }}
        >
          {locale === "zh-CN" ? "重试" : "Retry"}
        </Button>
      }
    />
  );

  return (
    <div className="portal-page-grid">
      <PageHeader
        eyebrow={project?.display_name ?? "LonMax AI"}
        title={locale === "zh-CN" ? "奖励与兑换" : "Rewards & redemption"}
        description={
          locale === "zh-CN"
            ? "推荐与签到由平台合规开关控制；所有奖励直接进入当前项目账本。"
            : "Referral and check-in programs are compliance-gated. Every award is credited directly to the current project ledger."
        }
      />

      {notice ? (
        <div className="portal-notice" role="status">
          <strong>{notice}</strong>
        </div>
      ) : null}

      {actionError ? (
        <div className="portal-form-error" role="alert">
          <strong>{locale === "zh-CN" ? "操作未完成" : "Action not completed"}</strong>
          <span>{actionError.message}</span>
          {actionError instanceof ApiError && actionError.requestId ? (
            <RequestId value={actionError.requestId} />
          ) : null}
        </div>
      ) : null}

      <div className="portal-metrics">
        <MetricCard
          tone="brand"
          label={locale === "zh-CN" ? "成功推荐" : "Successful referrals"}
          value={formatNumber(referral.data?.successful_referrals ?? 0, locale)}
        />
        <MetricCard
          tone="success"
          label={locale === "zh-CN" ? "推荐奖励" : "Referral awards"}
          value={`${formatNumber((referral.data?.earned_microunits ?? 0) / 1_000_000, locale)} ${currency}`}
        />
        <MetricCard
          tone="brand"
          label={locale === "zh-CN" ? "本月签到" : "Monthly check-ins"}
          value={formatNumber(checkins.data?.month_count ?? 0, locale)}
        />
        <MetricCard
          tone="success"
          label={locale === "zh-CN" ? "本月签到奖励" : "Monthly check-in awards"}
          value={`${formatNumber((checkins.data?.month_award_microunits ?? 0) / 1_000_000, locale)} ${currency}`}
        />
      </div>

      <div className="portal-growth-layout">
        <div className="portal-growth-main">
          <PageSection
            title={locale === "zh-CN" ? "推荐计划" : "Referral program"}
            description={
              locale === "zh-CN"
                ? "分享推荐码；奖励仅在平台规则和反滥用校验通过后结算。"
                : "Share the code. Awards settle only after platform policy and abuse checks pass."
            }
          >
            {referral.data ? (
              <div className="portal-referral-code">
                <code>{referral.data.referral_code}</code>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => void navigator.clipboard.writeText(referral.data!.referral_code)}
                >
                  <Copy size={14} />
                  {locale === "zh-CN" ? "复制" : "Copy"}
                </Button>
              </div>
            ) : (
              <StatePanel
                kind="empty"
                title={locale === "zh-CN" ? "推荐计划尚未启用" : "Referral program is not enabled"}
                description={
                  locale === "zh-CN"
                    ? "管理员确认合规配置并启用后，这里会生成你的推荐码。"
                    : "Your code appears after an administrator confirms compliance controls and enables the program."
                }
              />
            )}
          </PageSection>

          <PageSection title={locale === "zh-CN" ? "最近签到" : "Recent check-ins"}>
            {checkins.isLoading ? (
              <StatePanel kind="loading" title={locale === "zh-CN" ? "加载中…" : "Loading…"} />
            ) : (checkins.data?.recent?.length ?? 0) === 0 ? (
              <StatePanel
                kind="empty"
                title={locale === "zh-CN" ? "还没有打卡记录" : "No check-ins yet"}
                description={locale === "zh-CN" ? "每天打卡可以获得奖励额度。" : "Check in daily to earn bonus credit."}
              />
            ) : (
              checkins.data?.recent.map((item) => (
                <Row
                  key={item.id}
                  icon={<CalendarCheck size={19} />}
                  title={item.date}
                  detail={formatDate(item.created_at, locale)}
                  action={
                    <Badge tone="success">
                      +{formatNumber(item.awarded_microunits / 1_000_000, locale)}
                    </Badge>
                  }
                />
              ))
            )}
          </PageSection>        </div>

        <div className="portal-growth-sidebar">
          <PageSection
            title={locale === "zh-CN" ? "每日签到" : "Daily check-in"}
            description={
              locale === "zh-CN"
                ? "以 UTC 自然日为准，每位用户每天一次。"
                : "One check-in per user per UTC calendar day."
            }
            action={
              <Badge tone={checkins.data?.enabled ? "success" : "neutral"}>
                {checkins.data?.enabled
                  ? (locale === "zh-CN" ? "已启用" : "Enabled")
                  : (locale === "zh-CN" ? "未启用" : "Disabled")}
              </Badge>
            }
          >
            <Button
              disabled={!checkins.data?.enabled || checkins.data?.checked_in_today}
              busy={checkin.isPending}
              onClick={() => checkin.mutate()}
            >
              {checkins.data?.checked_in_today
                ? (locale === "zh-CN" ? "今日已签到" : "Checked in today")
                : (locale === "zh-CN" ? "立即签到" : "Check in")}
            </Button>
          </PageSection>

          <PageSection
            title={locale === "zh-CN" ? "兑换额度券" : "Redeem a credit voucher"}
            description={
              locale === "zh-CN"
                ? "兑换券只能使用一次；请确认当前项目后再提交。"
                : "A voucher can be used once. Confirm the current project before submitting."
            }
          >
            <form
              className="portal-redeem"
              onSubmit={(event: FormEvent) => { event.preventDefault(); redeem.mutate(); }}
            >
              <Field label={locale === "zh-CN" ? "兑换码" : "Voucher code"} required>
                <Input
                  autoComplete="off"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  required
                />
              </Field>
              <Button type="submit" busy={redeem.isPending} disabled={!code.trim()}>
                {locale === "zh-CN" ? "兑换到当前项目" : "Redeem to current project"}
              </Button>
            </form>
          </PageSection>
        </div>
      </div>
    </div>
  );
}

export function GrowthPage() {
  return <WorkspaceGuard><GrowthPageInner /></WorkspaceGuard>;
}
